import { motion } from "framer-motion";
import { Leaf } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export const LeafSprig = ({ className = "" }) => (
  <svg viewBox="0 0 120 200" fill="none" className={className} aria-hidden="true">
    <path d="M60 195 C56 145 58 80 62 8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const y = 40 + i * 26;
      return (
        <g key={i}>
          <path d={`M60 ${y} Q32 ${y - 12} 20 ${y - 32} Q44 ${y - 20} 60 ${y} Z`} fill="currentColor" opacity="0.9" />
          <path d={`M60 ${y - 6} Q88 ${y - 18} 100 ${y - 38} Q76 ${y - 26} 60 ${y - 6} Z`} fill="currentColor" opacity="0.55" />
        </g>
      );
    })}
  </svg>
);

export const Reveal = ({ children, delay = 0, className = "", y = 36 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const MaskedLines = ({ lines, className = "", lineClassName = "", as: Tag = "h1", delay = 0.2 }) => {
  const MotionTag = motion.create(Tag);
  return (
    <MotionTag className={className} aria-label={lines.join(" ")}>
      {lines.map((line, i) => (
        <span className="mask-line" key={i} aria-hidden="true">
          <motion.span
            className={Array.isArray(lineClassName) ? lineClassName[i] : lineClassName}
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, delay: delay + i * 0.13, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </MotionTag>
  );
};

export const SectionHeading = ({ kicker, title, className = "", tone = "dark" }) => (
  <Reveal className={className}>
    {kicker && (
      <p className={`${tone === "light" ? "label-mono-ink" : "label-mono"} mb-4 flex items-center gap-4`}>
        {kicker}
        <span className={`h-px w-20 ${tone === "light" ? "bg-gold-deep/60" : "bg-gold/50"}`} aria-hidden="true" />
      </p>
    )}
    <h2 className={`font-editorial text-3xl tracking-tight sm:text-4xl lg:text-5xl ${tone === "light" ? "text-ink" : "text-cream"}`}>
      {title}
    </h2>
  </Reveal>
);

export const GhostWord = ({ text, className = "" }) => (
  <span
    aria-hidden="true"
    className={`pointer-events-none select-none whitespace-nowrap font-editorial leading-none ${className}`}
    style={{ WebkitTextStroke: "1px rgba(212,175,55,0.14)", color: "transparent" }}
  >
    {text}
  </span>
);

export const GoldRule = ({ tone = "dark" }) => (
  <div className="mx-auto flex max-w-7xl items-center gap-5 px-5 py-2 sm:px-8" aria-hidden="true">
    <span className={`h-px flex-1 ${tone === "light" ? "bg-gold-deep/40" : "bg-gradient-to-r from-transparent via-gold/30 to-gold/10"}`} />
    <Leaf size={15} className={`shrink-0 ${tone === "light" ? "text-gold-deep/70" : "text-gold/60"}`} />
    <span className={`h-px flex-1 ${tone === "light" ? "bg-gold-deep/40" : "bg-gradient-to-l from-transparent via-gold/30 to-gold/10"}`} />
  </div>
);
